import { createRouter, createWebHistory } from 'vue-router'
import Tracker from '../views/Tracker.vue'
import store from "../store/store";
import { resolveLoginRedirectPath } from '../helpers/login-redirect';

function canUseEngageKit(currentUser) {
  if (!currentUser) {
    return false;
  }

  if (currentUser.user_metadata?.needs_provisioning === true) {
    return false;
  }

  if (currentUser.profile?.profile_data?.needs_provisioning === true) {
    return false;
  }

  return !!currentUser.app_metadata?.ek_token;
}

function canUseInlineManual(currentUser) {
  if (!currentUser) {
    return false;
  }

  if (currentUser.user_metadata?.needs_provisioning === true) {
    return false;
  }

  if (currentUser.profile?.profile_data?.needs_provisioning === true) {
    return false;
  }

  return true;
}

function isSuperUser(currentUser) {
  return currentUser?.app_metadata?.is_super_user === true;
}

const routes = [
  {
    path: '/feature-audit',
    name: 'Feature Audit',
    component: () => import(/* webpackChunkName: "featureAudit" */ '../views/FeatureAudit.vue'),
    meta: {
      title: 'Feature Audit',
      userRequired: true,
      superUserRequired: true,
    },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import(/* webpackChunkName: "reports" */ '../views/Reports.vue'),
    meta: {
      title: 'Reports',
      planRequired: true,
      userRequired: true
    },
  },
  {
    path: '/projects',
    name: 'Projects',
    component: () => import(/* webpackChunkName: "projects" */ '../views/Projects.vue'),
    meta: {
      title: 'Projects',
      userRequired: true
    },
  },
  {
    path: '/blocks',
    name: 'Blocks',
    component: () => import(/* webpackChunkName: "blocks" */ '../views/Blocks.vue'),
    meta: {
      title: 'Blocks',
      userRequired: true
    },
  },
  {
    path: '/blocks/new',
    name: 'Block Builder',
    component: () => import(/* webpackChunkName: "blockBuilder" */ '../views/BlockBuilder.vue'),
    meta: {
      title: 'Build a block',
      userRequired: true
    },
  },
  {
    path: '/',
    name: 'Tracker',
    component: Tracker,
    meta: {
      title: 'Tracker',
      planRequired: true,
      userRequired: true
    },
  },
  {
    path: '/build',
    name: 'Tracker Build',
    component: () => import(/* webpackChunkName: "trackerBuild" */ '../views/TrackerBuild.vue'),
    meta: {
      title: 'Tracker Build',
      planRequired: true,
      userRequired: true
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: () => import(/* webpackChunkName: "profile" */ '../views/Profile.vue'),
    meta: {
      title: 'Profile',
      userRequired: true
    }
  },
  {
    path: "/subscription",
    name: "Subscription",
    component: () => import(/* webpackChunkName: "subscription" */ '../views/Subscription.vue'),
    meta: {
      title: 'Subscription',
      userRequired: true
    }
  },
  {
    path: "/feedback",
    name: "Feedback",
    component: () => import(/* webpackChunkName: "feedback" */ '../views/Feedback.vue'),
    meta: {
      title: 'Feedback',
      userRequired: true
    }
  },
  {
    path: "/profile-edit",
    name: "Profile edit",
    component: () => import(/* webpackChunkName: "profileEdit" */ '../views/ProfileEdit.vue'),
    meta: {
      title: 'Profile edit',
      userRequired: true
    }
  },
  {
    path: "/add-calendar",
    name: "Add to calendar",
    component: () => import(/* webpackChunkName: "addCalendar" */ '../views/AddCalendar.vue'),
    meta: {
      title: 'Add to calendar',
      userRequired: true
    }
  },
  {
    path: "/share-social",
    name: "Share on social",
    component: () => import(/* webpackChunkName: "shareSocial" */ '../views/ShareSocial.vue'),
    meta: {
      title: 'Share on social',
      userRequired: true
    }
  },
  {
    path: "/status/:id",
    name: "Status",
    component: () => import(/* webpackChunkName: "status" */ '../views/Status.vue'),
    meta: {
      title: 'Status'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
    meta: {
      title: 'Login to continue',
      guestOnly: true,
    },
  },
  {
    path: '/signup',
    name: 'Sign up',
    component: () => import(/* webpackChunkName: "signup" */ '../views/SignUp.vue'),
    meta: {
      title: 'Sign up',
      guestOnly: true,
    },
  },
  {
    path: '/confirm-email',
    name: 'Confirm email',
    component: () => import(/* webpackChunkName: "confirmEmail" */ '../views/ConfirmEmail.vue'),
    meta: {
      title: 'Confirm email',
      guestOnly: true,
    }
  },
  {
    path: "/recover",
    name: "Recover password",
    component: () => import(/* webpackChunkName: "recover" */ '../views/RecoverAccount.vue'),
    meta: {
      title: 'Recover password',
      guestOnly: true,
    }
  },
  {
    path: '/:pathMatch(.*)',
    component: () => import(/* webpackChunkName: "notFound" */ '../views/NotFound.vue'),
    meta: {
      title: '404'
    }
  }
];

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'bg-gray-100 text-white',
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
  }
});

router.beforeEach(async (to, from, next) => {
  window.scrollTo(0, 0);
  if (typeof window.InlineManual !== 'undefined' && canUseInlineManual(store.getters["auth/currentUser"])) {
    window.InlineManual('update');
  }

  const guestOnly = to.matched.some(route => route.meta.guestOnly);
  const planRequired = to.matched.some(route => route.meta.planRequired);
  const userRequired = to.matched.some(route => route.meta.userRequired);
  const superUserRequired = to.matched.some(route => route.meta.superUserRequired);
  const isLoggedIn = store.getters["auth/loggedIn"];

  if (guestOnly && isLoggedIn) {
    return next(resolveLoginRedirectPath(store.getters["auth/currentUser"]));
  }

  // If the route doesnt have a `meta.planRequired` property go on ahead!
  if (!planRequired && !userRequired) {
    return next();
  }
  // If we go this far then it must have the `meta.authRequired`. But is there a user logged in? If so, then go right on ahead!
  if (isLoggedIn) {
    const currentUser = store.getters["auth/currentUser"];

    if (superUserRequired && !isSuperUser(currentUser)) {
      return next({ name: 'Tracker' });
    }

    // User is on free, show them Subscription page :)
    if (currentUser.app_metadata && currentUser.app_metadata.roles.length) {
      if (
        currentUser.app_metadata.roles[0] === 'free' &&
        to.name !== 'Subscription' &&
        to.name !== 'Profile edit' &&
        to.name !== 'Profile'
      ) {
        return next({ name: "Subscription", query: { redirectFrom: to.fullPath } });
      }

      if (currentUser.lock && to.name !== 'Subscription' && to.name !== 'Profile edit' && to.name !== 'Profile') {
        return next({ name: "Subscription", query: { redirectFrom: to.fullPath } });
      }
    }
  } else {
    return next({ name: "Login", query: { redirectFrom: to.fullPath } });
  }
  return next();
});


router.afterEach((to) => {
  if (to.meta && to.meta.title) {
    document.title = to.meta.title + ' | Kairo';
  }

  if (store.getters["auth/loggedIn"]) {
    const currentUser = store.getters["auth/currentUser"];
    if (currentUser.app_metadata && currentUser.app_metadata.roles.length) {
      if (currentUser.app_metadata && canUseEngageKit(currentUser)) {
        if (typeof window.EngageKit !== 'undefined') {   
          window.EngageKit.ready(() => {
            window.EngageKit.track(
              'Page',
              {
                pageUrl: window.location.href,
                pageUrlHost: window.location.hostname,
                pageUrlPath: window.location.pathname,
                pageUrlQuery: window.location.search,
                pageUrlHash: window.location.hash,
                pageTitle: to.meta.title,
              }
            )
          });
        }
      }
    }
  }

});

export default router
